// ═══════════════════════════════════════════════════════════════
// CONTEXT: Company Context
// Maneja la compañía actual seleccionada por el usuario
// ═══════════════════════════════════════════════════════════════

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { companiesAPI } from '../api/companies';
import { authAPI } from '../api/auth';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany debe usarse dentro de CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  // 'idle' | 'fading-out' | 'switching' | 'fading-in'
  const [switchPhase, setSwitchPhase] = useState('idle');

  // Cargar compañías del usuario al montar
  useEffect(() => {
    loadUserCompanies();
  }, []);

  const loadUserCompanies = async () => {
    try {
      setLoading(true);
      console.log('🏢 Cargando compañías del usuario...');
      
      const data = await companiesAPI.getMyCompanies();
      
      if (!Array.isArray(data) || data.length === 0) {
        console.warn('⚠️ Usuario no tiene acceso a ninguna compañía');
        setCompanies([]);
        setCurrentCompany(null);
        setLoading(false);
        return;
      }
      
      setCompanies(data);
      
      // Intentar cargar la última compañía seleccionada
      const savedCompanyId = localStorage.getItem('currentCompanyId');
      const savedCompany = data.find(c => c.id === savedCompanyId);
      
      if (savedCompany) {
        console.log('✅ Restaurando compañía:', savedCompany.nombre);
        setCurrentCompany(savedCompany);
        localStorage.setItem('currentCompanyId', savedCompany.id);
        localStorage.setItem('companyId', savedCompany.id); // Para API interceptor
      } else {
        // Si no hay guardada, usar la primera
        console.log('✅ Usando primera compañía:', data[0].nombre);
        setCurrentCompany(data[0]);
        localStorage.setItem('currentCompanyId', data[0].id);
        localStorage.setItem('companyId', data[0].id); // Para API interceptor
      }
      
      console.log('✅ Compañías cargadas:', data.length);
    } catch (error) {
      console.error('❌ Error cargando compañías:', error);
      setCompanies([]);
      setCurrentCompany(null);
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = useCallback(async (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company || company.id === currentCompany?.id) return;

    try {
      // Fase 1: fade-out del contenido actual
      setSwitchPhase('fading-out');
      await authAPI.switchCompany(companyId);

      // Esperar que la animación de salida termine
      await new Promise(r => setTimeout(r, 300));

      // Fase 2: actualizar compañía (React desmonta/monta módulos via key)
      setSwitchPhase('switching');
      setCurrentCompany(company);
      localStorage.setItem('currentCompanyId', company.id);
      localStorage.setItem('companyId', company.id);

      // Breve pausa para que React procese el cambio de key
      await new Promise(r => setTimeout(r, 50));

      // Fase 3: fade-in del nuevo contenido
      setSwitchPhase('fading-in');
      await new Promise(r => setTimeout(r, 350));

      setSwitchPhase('idle');
    } catch (error) {
      console.error('❌ Error al cambiar de compañía:', error);
      setSwitchPhase('idle');
      alert('Error al cambiar de compañía. Por favor intenta nuevamente.');
    }
  }, [companies, currentCompany]);

  const refreshCompanies = async () => {
    await loadUserCompanies();
  };

  const value = {
    currentCompany,
    companies,
    loading,
    switchCompany,
    refreshCompanies,
    switchPhase,
    companyId:   currentCompany?.id   || null,
    companyName: currentCompany?.nombre || null,
    companyRole: currentCompany?.role || null,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
